import { useState, useEffect, useRef } from "react";
import * as signalR from "@microsoft/signalr";
import { X, MessageSquare, Edit, Save, Hash, FileText, Trophy } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { useAuth } from "../../../../context/AuthContext";
import { PreguntaAsambleaRecord } from "../../../../api/preguntaAsambleaService";
import {
  obtenerRespuestasPreguntaAsamblea,
  modificarRespuestaPreguntaAsamblea,
  modificarEstatusRespuestaPreguntaAsamblea,
  RespuestaPreguntaAsambleaRecord,
} from "../../../../api/respuestaPreguntaAsambleaService";
import {
  obtenerEstatusRespuestaPreguntaAsamblea,
  EstatusRespuestaPreguntaAsamblea,
} from "../../../../api/estatusRespuestaPreguntaAsambleaService";
import { HUB_BASE_URL } from "../../../../api/signalRConfig";
import Swal from "sweetalert2";

interface DetailRespuestaPreguntaAsambleaModalProps {
  isOpen: boolean;
  respuesta: RespuestaPreguntaAsambleaRecord | null;
  pregunta: PreguntaAsambleaRecord | null;
  onClose: (shouldReload?: boolean) => void;
}

export default function DetailRespuestaPreguntaAsambleaModal({
  isOpen,
  respuesta,
  pregunta,
  onClose,
}: DetailRespuestaPreguntaAsambleaModalProps) {
  const { session } = useAuth();
  const lastLoadedRespuestaRef = useRef<string>("");
  const isLoadingRef = useRef(false);

  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [respuestaData, setRespuestaData] = useState<RespuestaPreguntaAsambleaRecord | null>(respuesta);

  const [orden, setOrden] = useState("");
  const [texto, setTexto] = useState("");
  const [selectedEstatus, setSelectedEstatus] = useState("");

  const [estatusList, setEstatusList] = useState<EstatusRespuestaPreguntaAsamblea[]>([]);
  const [isLoadingEstatus, setIsLoadingEstatus] = useState(false);

  const loadRespuestaDetails = async () => {
    if (!respuesta?.rpa_codigo || !pregunta?.pra_codigo) return;

    try {
      setIsLoadingDetails(true);
      const response = await obtenerRespuestasPreguntaAsamblea({
        pregunta_codigo: String(pregunta.pra_codigo),
        codigo: String(respuesta.rpa_codigo),
        estatus_codigo: "",
      });

      if (response.code === "1" && Array.isArray(response.object_) && response.object_.length > 0) {
        const data = response.object_[0];
        setRespuestaData(data);
        setOrden(data.rpa_orden);
        setTexto(data.rpa_texto);
        setSelectedEstatus(data.rpa_estatus || "");
      }
    } catch (error) {
      console.error("Error al cargar detalles de respuesta:", error);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  useEffect(() => {
    if (isOpen && respuesta) {
      const respuestaId = String(respuesta.rpa_codigo || "");
      const isNewRespuesta = lastLoadedRespuestaRef.current !== respuestaId;

      if (isNewRespuesta && !isLoadingRef.current) {
        lastLoadedRespuestaRef.current = respuestaId;
        loadRespuestaDetails().then(() => {
          loadEstatus();
        });
      } else if (!isNewRespuesta) {
        setRespuestaData(respuesta);
        setOrden(respuesta.rpa_orden);
        setTexto(respuesta.rpa_texto);
        setSelectedEstatus(respuesta.rpa_estatus || "");
      }
    } else {
      setEstatusList([]);
      lastLoadedRespuestaRef.current = "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, respuesta?.rpa_codigo]);

  useEffect(() => {
    if (!isOpen || !respuesta?.rpa_codigo || !pregunta?.pra_codigo) return;

    const preguntaCodigo = pregunta.pra_codigo.toString();
    const respuestaCodigo = respuesta.rpa_codigo.toString();
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${HUB_BASE_URL}/respuestahub`, { withCredentials: true })
      .withAutomaticReconnect()
      .build();

    connection.on("cambioEnRespuestaDeAsamblea", async (codigoPregunta: string, codigoRespuesta: string) => {
      if (respuestaCodigo !== codigoRespuesta) return;
      try {
        const response = await obtenerRespuestasPreguntaAsamblea({
          pregunta_codigo: codigoPregunta,
          codigo: codigoRespuesta,
          estatus_codigo: "",
        });
        if (response.code === "1" && Array.isArray(response.object_) && response.object_.length > 0) {
          const data = response.object_[0];
          if (data && String(data.rpa_codigo) !== "0") {
            setRespuestaData(data);
            setOrden(data.rpa_orden);
            setTexto(data.rpa_texto);
            setSelectedEstatus(data.rpa_estatus || "");
          }
        }
      } catch (err) {
        if (err instanceof Error && process.env.NODE_ENV === "development") {
          console.error("[RespuestaHub]", err.message);
        }
      }
    });

    const start = async () => {
      try {
        await connection.start();
        await connection.invoke("UnirseAGrupoRespuestaDePreguntaDeAsamblea", preguntaCodigo, respuestaCodigo);
      } catch (err) {
        if (err instanceof Error && process.env.NODE_ENV === "development") {
          console.error("[RespuestaHub]", err.message);
        }
      }
    };

    start();

    return () => {
      if (connection.state !== signalR.HubConnectionState.Disconnected) {
        connection.stop();
      }
    };
  }, [isOpen, respuesta?.rpa_codigo, pregunta?.pra_codigo]);

  const loadEstatus = async () => {
    if (isLoadingRef.current) return;

    try {
      isLoadingRef.current = true;
      setIsLoadingEstatus(true);

      const estatusResponse = await obtenerEstatusRespuestaPreguntaAsamblea();
      if (estatusResponse.code === "1" && estatusResponse.object_) {
        setEstatusList(estatusResponse.object_);
      }
      setIsLoadingEstatus(false);
    } catch (error) {
      console.error("Error al cargar estatus:", error);
    } finally {
      isLoadingRef.current = false;
    }
  };

  const handleSaveChanges = async () => {
    if (!orden.trim()) {
      await Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor ingrese la nomenclatura",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    if (!texto.trim()) {
      await Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor ingrese el texto de la respuesta",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const usuarioAdministrador = session?.email || "";
      const codigo = String(respuestaData?.rpa_codigo || "");
      const preguntaCodigo = String(pregunta?.pra_codigo || "");

      if (!usuarioAdministrador || !codigo || !preguntaCodigo) {
        throw new Error("No se pudo obtener la información necesaria");
      }

      const payload = {
        usuario_administrador: usuarioAdministrador,
        codigo,
        pregunta_codigo: preguntaCodigo,
        texto: texto.trim(),
        orden: orden.trim(),
      };

      const response = await modificarRespuestaPreguntaAsamblea(payload);

      if (response.code === "1" || response.code === 1) {
        await Swal.fire({
          icon: "success",
          title: "¡Cambios guardados!",
          text: response.message || "La respuesta se ha actualizado exitosamente",
          confirmButtonColor: "#3d5a40",
          timer: 2000,
          timerProgressBar: true,
          didOpen: (popup) => {
            (popup as HTMLElement).style.zIndex = '10001';
          },
        });
        setIsEditing(false);
        await loadRespuestaDetails();
        onClose(true);
      } else {
        throw new Error(response.message || "Error al actualizar la respuesta");
      }
    } catch (error) {
      console.error("Error al guardar cambios:", error);
      const errorMessage = error instanceof Error ? error.message : "Error al actualizar la respuesta";
      const mensajeTraducido = errorMessage.includes("Request failed")
        ? "Error al actualizar la respuesta. Por favor, intente nuevamente."
        : errorMessage.includes("Network Error")
        ? "Error de conexión. Verifique su conexión a internet."
        : errorMessage;

      await Swal.fire({
        icon: "error",
        title: "Error",
        text: mensajeTraducido,
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEstatusChange = async (estatusCodigo: string) => {
    const previousEstatus = selectedEstatus;
    setSelectedEstatus(estatusCodigo);

    try {
      const usuarioAdministrador = session?.email || "";
      const codigo = String(respuestaData?.rpa_codigo || "");
      const preguntaCodigo = String(pregunta?.pra_codigo || "");

      if (!usuarioAdministrador || !codigo || !preguntaCodigo) {
        throw new Error("No se pudo obtener la información necesaria");
      }

      const payload = {
        usuario_administrador: usuarioAdministrador,
        codigo,
        pregunta_codigo: preguntaCodigo,
        estatus_codigo: estatusCodigo,
      };

      const response = await modificarEstatusRespuestaPreguntaAsamblea(payload);

      if (response.code !== "1" && response.code !== 1) {
        setSelectedEstatus(previousEstatus);
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: response.message || "No se pudo cambiar el estatus",
          confirmButtonColor: "#d33",
          didOpen: (popup) => {
            (popup as HTMLElement).style.zIndex = '10001';
          },
        });
      } else {
        await loadRespuestaDetails();
      }
    } catch (error) {
      setSelectedEstatus(previousEstatus);
      console.error("Error al cambiar estatus:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo cambiar el estatus",
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    }
  };

  if (!isOpen || !respuesta || !pregunta) return null;

  const respuestaMostrar = respuestaData || respuesta;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="bg-white border-b px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-military-600 flex items-center gap-2">
              <MessageSquare size={28} />
              Detalle de Respuesta
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Código: #{respuestaMostrar.rpa_codigo}
            </p>
          </div>
          <button
            onClick={() => onClose(false)}
            className="text-gray-400 hover:text-gray-600 transition"
            disabled={isSubmitting || isLoadingDetails}
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {isLoadingDetails ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-t-military-500 border-gray-300 mb-4" />
              <p className="text-gray-600">Cargando detalles...</p>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FileText size={16} className="inline mr-1" />
                  Texto de la pregunta
                </label>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-lg font-bold text-black">
                    {respuestaMostrar.rpa_pregunta_texto || pregunta.pra_texto}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estatus
                </label>
                {isLoadingEstatus ? (
                  <div className="flex items-center gap-2 text-gray-500">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-t-military-500 border-gray-300" />
                    <span className="text-sm">Cargando estatus...</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {estatusList.map((estatus) => (
                      <label
                        key={estatus.erp_codigo}
                        className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="estatus"
                          value={estatus.erp_codigo}
                          checked={selectedEstatus === estatus.erp_codigo}
                          onChange={(e) => handleEstatusChange(e.target.value)}
                          disabled={isSubmitting}
                          className="text-military-500 focus:ring-military-500"
                        />
                        <span className="text-sm font-medium text-gray-700">
                          {estatus.erp_nombre}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {respuestaMostrar.rpa_ganadora === "1" && (
                <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <Trophy size={20} className="text-yellow-600" />
                    <div>
                      <p className="text-sm font-semibold text-yellow-900">
                        Respuesta Ganadora
                      </p>
                      <p className="text-xs text-yellow-700 mt-1">
                        Esta respuesta recibió la mayor cantidad de votos de los residentes
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Votos recibidos
                </label>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-lg font-bold text-gray-900">
                    {respuestaMostrar.erp_total ?? 0} {respuestaMostrar.erp_total === 1 ? "voto" : "votos"}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Hash size={16} className="inline mr-1" />
                  Nomenclatura (Letra o Número) <span className="text-red-500">*</span>
                </label>
                {isEditing ? (
                  <Input
                    type="text"
                    value={orden}
                    onChange={(e) => setOrden(e.target.value)}
                    placeholder="Ej: 1, 2, 3, A, B, C..."
                    disabled={isSubmitting}
                    required
                    maxLength={10}
                  />
                ) : (
                  <Input
                    value={respuestaMostrar.rpa_orden}
                    disabled
                    className="bg-gray-50"
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MessageSquare size={16} className="inline mr-1" />
                  Texto de la respuesta <span className="text-red-500">*</span>
                </label>
                {isEditing ? (
                  <Textarea
                    value={texto}
                    onChange={(e) => setTexto(e.target.value)}
                    disabled={isSubmitting}
                    className="min-h-[100px]"
                    required
                  />
                ) : (
                  <Textarea
                    value={respuestaMostrar.rpa_texto}
                    disabled
                    className="bg-gray-50 min-h-[100px]"
                  />
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t">
                {isEditing ? (
                  <>
                    <Button
                      onClick={() => {
                        setIsEditing(false);
                        setOrden(respuestaMostrar.rpa_orden);
                        setTexto(respuestaMostrar.rpa_texto);
                      }}
                      variant="outline"
                      disabled={isSubmitting}
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleSaveChanges}
                      disabled={isSubmitting}
                      className="flex-1 bg-military-500 hover:bg-military-600 text-white"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                          Guardando...
                        </>
                      ) : (
                        <>
                          <Save size={20} className="mr-2" />
                          Guardar Cambios
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      onClick={() => setIsEditing(true)}
                      variant="outline"
                      className="flex-1 border-military-500 text-military-600 hover:bg-military-50"
                    >
                      <Edit size={20} className="mr-2" />
                      Modificar
                    </Button>
                    <Button
                      onClick={() => onClose(false)}
                      variant="outline"
                      className="flex-1"
                    >
                      Cerrar
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
